/**
 * Profilseite – /profile
 *
 * Zeigt alle wichtigen Charakter-Informationen des eingeloggten Spielers.
 * Zugänglich via Klick auf den Usernamen im Dashboard-Header oder direkt.
 *
 * Design: Terminal-UI, Share Tech Mono, keine Emojis, keine Gradienten.
 */

import '../styles/dashboard.css';
import { navigate } from '../router.js';
import { gameState, type PlayerSnapshot } from '../state/gameState.js';
import { api, ApiError } from '../services/api.js';
import { renderPlayerBadges, getPrestigeColorVar } from '../utils/award-badges.js';
import { getWantedLevelLabel, getNextWantedThreshold } from '../utils/wanted-labels.js';
import { mountPrestigeSection } from '../components/PrestigeSection.js';

// ─── Auszeichnungs-System (NT-TITLES-01) ───────────────────────────────────────

interface PlayerAwardsResponse {
  badges: unknown[]; // ungenutzt hier — Badges werden bereits client-seitig aus PlayerSnapshot komponiert
  availableTitles: { awardKey: string; label: string }[];
  selectedTitleAwardKey: string | null;
}

// ─── Lokale Typen ─────────────────────────────────────────────────────────────

interface ProfileJobState {
  jobLevel: number;
  jobXp: number;
}

interface ProfileJobItem {
  id: string;
  name: string;
  phase: number;
  playerState: ProfileJobState;
}

interface ProfileJobStatusResponse {
  allJobs: ProfileJobItem[];
}

// ─── Hilfsfunktionen ─────────────────────────────────────────────────────────

function esc(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');
}

function xpNeeded(level: number): number {
  return Math.floor(25 * Math.pow(level, 1.7));
}

function jobXpNeeded(level: number): number {
  return Math.floor(400 * Math.pow(level, 1.5));
}

function formatCents(centStr: string): string {
  const val = parseFloat(centStr) / 100;
  return val.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function wantedDots(level: number): string {
  return Array.from({ length: 5 }, (_, i) => (i < level ? '●' : '○')).join('');
}

function formatDate(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString('de-DE', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
}

function romanPrestige(n: number): string {
  const numerals = ['I', 'II', 'III'];
  return numerals[n - 1] ?? String(n);
}

// Badges kommen seit NT-TITLES-01 aus der zentralen award-badges.ts — ersetzt die vormals
// hier lokale, von der Rangliste abweichende Farb-Zuordnung (derselbe Crime-Rang zeigte
// vorher in Profil und Rangliste unterschiedliche Farben).
function buildBadges(player: PlayerSnapshot): string {
  const html = renderPlayerBadges(
    {
      isAlphaTester: player.isAlphaTester,
      hasFrueherVogelBadge: player.hasFrueherVogelBadge,
      crimeRankColorVar: player.crimeRankColorVar,
      crimeRankLabel: player.crimeRankLabel,
      prestigeRank: player.prestigeRank,
    },
    'profile-badge',
  );
  if (html === '') return '';
  return `<div class="profile-badge-row">${html}</div>`;
}

// ─── Render ───────────────────────────────────────────────────────────────────

function buildProfileHtml(player: PlayerSnapshot, jobs: ProfileJobItem[]): string {
  const xpVal = parseInt(player.xp, 10);
  const restedXpVal = parseInt(player.restedXp, 10);
  const xpMax = xpNeeded(player.level);
  const xpPct = Math.min(100, Math.floor((xpVal / xpMax) * 100));
  const wanted = player.wantedLevel;
  const wantedPoints = player.wantedPoints ?? 0;
  const nextThreshold = getNextWantedThreshold(wanted);
  const MAX_WANTED_POINTS = 600;
  const wantedColor = wanted >= 4 ? 'var(--accent-red)'
    : wanted >= 2 ? 'var(--accent-orange)'
    : wanted >= 1 ? 'var(--accent-gold)'
    : 'var(--text-muted)';
  const prestige = player.prestigeRank ?? 0;

  // Jobs mit Level > 1 sortiert nach Job-Level (absteigend), max. 8 anzeigen
  const activeJobs = [...jobs]
    .filter((j) => j.playerState.jobLevel > 1)
    .sort((a, b) => b.playerState.jobLevel - a.playerState.jobLevel)
    .slice(0, 8);

  // Mitgliedschaftsdauer in Tagen berechnen
  const memberDays = player.createdAt !== undefined
    ? Math.floor((Date.now() - new Date(player.createdAt).getTime()) / 86_400_000)
    : null;

  return `
    <div class="profile-page" id="profile-root">
      <header class="dash-header">
        <span class="dash-brand">&#9654; NIGHTTRADE</span>
        <span style="flex:1"></span>
        <button class="dash-nav-action" id="profile-back-btn">← DASHBOARD</button>
      </header>

      <main class="profile-main">
        <div class="profile-grid">

          <!-- ─── Charakter-Hero ─── -->
          <section class="profile-card profile-card--hero">
            <div class="profile-hero-top">
              <div>
                <div class="profile-hero-name">
                  ${esc(player.username)}
                </div>
                <div class="profile-hero-level">
                  LEVEL <span class="val-gold">${String(player.level)}</span>
                </div>
                ${buildBadges(player)}
                <div class="profile-title-picker" id="profile-title-picker" hidden></div>
              </div>
              ${memberDays !== null ? `
                <div class="profile-member-badge">
                  <span class="profile-member-days">${String(memberDays)}</span>
                  <span class="profile-member-label">TAGE</span>
                </div>
              ` : ''}
            </div>

            <div class="profile-xp-section">
              <div class="profile-xp-label">
                <span>ERFAHRUNG</span>
                <span class="val-gold">${xpVal.toLocaleString('de-DE')}&nbsp;/&nbsp;${xpMax.toLocaleString('de-DE')}&nbsp;XP</span>
              </div>
              <div class="profile-xp-bar" title="${String(xpPct)}%">
                <div class="profile-xp-fill" style="width:${String(xpPct)}%"></div>
              </div>
              <div class="profile-xp-pct">noch&nbsp;${(xpMax - xpVal).toLocaleString('de-DE')}&nbsp;XP bis Level&nbsp;${String(player.level + 1)}</div>
            </div>

            ${restedXpVal > 0 ? `
              <div class="profile-rested">
                <span class="profile-rested-label">RESTED XP</span>
                <span class="val-purple">+${restedXpVal.toLocaleString('de-DE')}&nbsp;XP</span>
                <span class="profile-rested-hint">+20% Bonus aktiv</span>
              </div>
            ` : ''}
          </section>

          <!-- ─── Status ─── -->
          <section class="profile-card">
            <h3 class="profile-card-title">STATUS</h3>
            <div class="profile-stat-row">
              <span>Fahndungslevel</span>
              <span class="${wanted >= 4 ? 'val-red' : wanted > 0 ? 'val-orange' : 'val-muted'}">${wantedDots(wanted)}&nbsp;${getWantedLevelLabel(wanted)}</span>
            </div>
            <div style="margin-top:6px;">
              <button
                class="sidebar-wanted-btn"
                id="profile-wanted-btn"
                data-wanted-toggle="profile-wanted-detail"
                data-wanted-arrow="profile-wanted-arrow"
                title="Fahndungsdetails anzeigen"
              >
                <span class="sidebar-wanted-dots-row">${wantedDots(wanted)}</span>
                <span style="font-size:12px;letter-spacing:0.06em;color:${wantedColor};font-weight:bold;">${getWantedLevelLabel(wanted)}</span>
                <span style="font-size:10px;color:var(--text-muted);margin-left:auto;" id="profile-wanted-arrow">▼</span>
              </button>
              <div class="sidebar-wanted-detail" id="profile-wanted-detail" style="display:none;">
                <div class="sidebar-detail-row">
                  <span>Punkte</span>
                  <span style="color:${wantedColor};">${String(wantedPoints)} / ${String(MAX_WANTED_POINTS)}</span>
                </div>
                ${nextThreshold !== null ? `<div class="sidebar-detail-row"><span>${getWantedLevelLabel(wanted + 1)} ab</span><span>+${String(Math.max(0, nextThreshold - wantedPoints))} P</span></div>` : `<div class="sidebar-detail-row"><span>Puffer bis Max</span><span style="color:var(--accent-red);">${String(Math.max(0, 600 - wantedPoints))} P</span></div>`}
                ${wantedPoints > 0 ? `<div class="sidebar-detail-row"><span>Abbau</span><span>~${String(wantedPoints)} Min (-1P/Min)</span></div>` : ''}
                ${wanted === 0 && wantedPoints === 0 ? `<div class="sidebar-detail-row" style="color:var(--accent-green);">SAUBER</div>` : ''}
              </div>
            </div>
            <div class="profile-stat-row">
              <span>Reputation</span>
              <span>${String(player.reputation ?? 0)}&nbsp;/&nbsp;1000</span>
            </div>
            ${prestige > 0 ? `
              <div class="profile-stat-row">
                <span>Prestige</span>
                <span style="color:var(${getPrestigeColorVar(prestige)});">${romanPrestige(prestige)}</span>
              </div>
            ` : ''}
          </section>

          <!-- ─── Finanzen ─── -->
          <section class="profile-card">
            <h3 class="profile-card-title">FINANZEN</h3>
            <div class="profile-stat-row">
              <span>Bargeld</span>
              <span class="val-gold">${formatCents(player.noxBalance)}&nbsp;&#8358;</span>
            </div>
            <div class="profile-stat-row">
              <span>Schwarzgeld</span>
              <span class="val-bm">${formatCents(player.blackMoney)}&nbsp;S-&#8358;</span>
            </div>
            <div class="profile-stat-row">
              <span>Scheine</span>
              <span class="val-scheine">${String(player.scheine ?? 0)}</span>
            </div>
            ${player.politicalInfluence !== undefined ? `
              <div class="profile-stat-row">
                <span>Politischer Einfluss</span>
                <span class="val-purple">${String(player.politicalInfluence)}&nbsp;/&nbsp;1000</span>
              </div>
            ` : ''}
          </section>

          <!-- ─── Account ─── -->
          <section class="profile-card">
            <h3 class="profile-card-title">ACCOUNT</h3>
            ${player.createdAt !== undefined ? `
              <div class="profile-stat-row">
                <span>Mitglied seit</span>
                <span>${formatDate(player.createdAt)}</span>
              </div>
            ` : ''}
            <div class="profile-stat-row">
              <span>Zuletzt aktiv</span>
              <span>${formatDate(player.lastSeenAt)}</span>
            </div>
          </section>

          <!-- ─── Job-Erfahrung ─── -->
          ${activeJobs.length > 0 ? `
            <section class="profile-card profile-card--full">
              <h3 class="profile-card-title">JOB-ERFAHRUNG</h3>
              <div class="profile-job-list">
                ${activeJobs.map((j) => {
                  const jLvl = j.playerState.jobLevel;
                  const jXp  = j.playerState.jobXp;
                  const jMax = jobXpNeeded(jLvl);
                  const jPct = Math.min(100, Math.floor((jXp / jMax) * 100));
                  return `
                    <div class="profile-job-row">
                      <span class="profile-job-name">${esc(j.name)}</span>
                      <span class="profile-job-level val-purple">LVL&nbsp;${String(jLvl)}</span>
                      <div class="profile-job-bar" title="${jXp.toLocaleString('de-DE')} / ${jMax.toLocaleString('de-DE')} Job-XP">
                        <div class="profile-job-bar-fill" style="width:${String(jPct)}%"></div>
                      </div>
                      <span class="profile-job-pct val-muted">${String(jPct)}%</span>
                    </div>
                  `;
                }).join('')}
              </div>
            </section>
          ` : ''}

          <!-- ─── Prestige (NT-PRESTIGE-01) — bleibt versteckt bei PRESTIGE_MODE=OFF ─── -->
          <section class="profile-card profile-card--full prs-card" id="profile-prestige-section" hidden></section>

          <!-- ─── Shop ─── -->
          <section class="profile-card profile-card--full">
            <h3 class="profile-card-title">SHOP</h3>
            <div style="display:flex;align-items:center;justify-content:space-between;flex-wrap:wrap;gap:var(--space-3);">
              <p style="font-family:var(--font-mono);font-size:var(--font-size-sm);color:var(--text-muted);margin:0;max-width:60ch;">
                Gute Kontakte öffnen neue Türen. Sichere dir Scheine, Pässe und exklusive Vorteile, während du deinen Einfluss im Netzwerk ausbaust.
              </p>
              <a
                href="/shop"
                style="
                  font-family:var(--font-mono);font-size:var(--font-size-xs);letter-spacing:0.08em;text-transform:uppercase;
                  padding:var(--space-3) var(--space-4);background:transparent;border-radius:var(--radius-btn);
                  border:1px solid var(--accent-gold);color:var(--accent-gold);cursor:pointer;
                  min-height:44px;display:flex;align-items:center;text-decoration:none;white-space:nowrap;
                "
              >ÖFFNEN</a>
            </div>
          </section>

          <!-- ─── Einstellungen ─── -->
          <section class="profile-card profile-card--full">
            <h3 class="profile-card-title">EINSTELLUNGEN</h3>
            <div style="display:flex;align-items:center;justify-content:space-between;flex-wrap:wrap;gap:var(--space-2);">
              <div>
                <p style="font-family:var(--font-mono);font-size:11px;color:var(--text-muted);margin:0 0 2px 0;letter-spacing:0.05em;">BENACHRICHTIGUNGEN</p>
                <p style="font-family:var(--font-mono);font-size:10px;color:var(--text-muted);margin:0;opacity:0.7;">Push nach Kategorie ein-/ausschalten (Jobs, Gewächshaus, Labor, ...)</p>
              </div>
              <a
                href="/settings"
                style="
                  font-family:var(--font-mono);font-size:11px;letter-spacing:0.08em;text-transform:uppercase;
                  padding:var(--space-2) var(--space-3);background:transparent;border-radius:0;
                  border:1px solid var(--border);color:var(--text-muted);cursor:pointer;
                  min-height:44px;display:flex;align-items:center;text-decoration:none;white-space:nowrap;
                "
              >EINSTELLEN</a>
            </div>
          </section>

        </div>
      </main>
    </div>
  `;
}

function setupProfileWantedToggle(root: ParentNode = document): void {
  const btn = root.querySelector<HTMLElement>('#profile-wanted-btn');
  const detail = root.querySelector<HTMLElement>('#profile-wanted-detail');
  const arrow = root.querySelector<HTMLElement>('#profile-wanted-arrow');
  if (!btn || !detail || !arrow) return;

  btn.addEventListener('click', (event) => {
    event.preventDefault();
    const isOpen = detail.style.display !== 'none';
    detail.style.display = isOpen ? 'none' : 'block';
    arrow.textContent = isOpen ? '▼' : '▲';
  });
}

async function loadProfileData(): Promise<{ player: PlayerSnapshot; jobs: ProfileJobItem[] }> {
  const [player, jobStatus] = await Promise.all([
    api.get<PlayerSnapshot>('/auth/me'),
    api.get<ProfileJobStatusResponse>('/jobs/status'),
  ]);

  gameState.setPlayer(player);
  return { player, jobs: jobStatus.allJobs };
}

export function renderProfileTab(): string {
  return `
    <section class="dash-content">
      <div class="profile-main" id="profile-tab-root">
        <div class="profile-loading">LADE PROFIL...</div>
      </div>
    </section>
  `;
}

// ─── Titel-Auswahl (NT-TITLES-01) ───────────────────────────────────────────────
// Aktuell existiert noch keine kind=TITLE-Auszeichnung (nur Alpha-Tester/Früher-Vogel/
// Crime-Rang, alle kind=BADGE, automatisch angezeigt, nicht wählbar) — die Sektion bleibt
// deshalb `hidden`, bis der Endpunkt tatsächlich Titel liefert (künftige Treuestufen/
// Abo-Tiers). Kein leerer Picker für ein Feature mit null aktuellen Mitgliedern.
async function mountTitlePicker(root: ParentNode): Promise<void> {
  const container = root.querySelector<HTMLElement>('#profile-title-picker');
  if (!container) return;

  try {
    const res = await api.get<PlayerAwardsResponse>('/awards/me');
    if (res.availableTitles.length === 0) return;

    const options = res.availableTitles
      .map((t) => `<option value="${esc(t.awardKey)}"${t.awardKey === res.selectedTitleAwardKey ? ' selected' : ''}>${esc(t.label)}</option>`)
      .join('');

    container.innerHTML = `
      <label class="profile-title-picker-label" for="profile-title-select">TITEL</label>
      <select id="profile-title-select" class="profile-title-picker-select">
        <option value=""${res.selectedTitleAwardKey === null ? ' selected' : ''}>— kein Titel —</option>
        ${options}
      </select>
    `;
    container.hidden = false;

    const select = container.querySelector<HTMLSelectElement>('#profile-title-select');
    select?.addEventListener('change', () => {
      void api.post('/awards/select-title', { awardKey: select.value === '' ? null : select.value });
    });
  } catch {
    // Titel-Auswahl ist ein nice-to-have — kein Fehler-UI, Sektion bleibt einfach versteckt.
  }
}

export async function mountProfileTab(): Promise<void> {
  const root = document.getElementById('profile-tab-root');
  if (!root) return;

  try {
    const { player, jobs } = await loadProfileData();
    const temp = document.createElement('div');
    temp.innerHTML = buildProfileHtml(player, jobs);
    const profileMain = temp.querySelector<HTMLElement>('.profile-main');

    if (profileMain !== null) {
      root.innerHTML = profileMain.innerHTML;
      void mountTitlePicker(root);
      void mountPrestigeSection(root, () => { void mountProfileTab(); });
      return;
    }

    root.innerHTML = '<div style="padding:2rem;color:var(--accent-red)">Profil konnte nicht geladen werden.</div>';
  } catch (err) {
    if (err instanceof ApiError) {
      root.innerHTML = `<div style="padding:2rem;color:var(--accent-red)">${esc(err.message)}</div>`;
    } else {
      root.innerHTML = '<div style="padding:2rem;color:var(--accent-red)">Profil konnte nicht geladen werden.</div>';
    }
  }
}

// ─── Einstiegspunkt ───────────────────────────────────────────────────────────

export async function renderProfilePage(): Promise<void> {
  if (!gameState.isAuthenticated()) {
    navigate('/login');
    return;
  }

  const app = document.getElementById('app');
  if (!app) return;

  app.innerHTML = '<div class="profile-loading">LADE PROFIL...</div>';

  try {
    const { player, jobs } = await loadProfileData();
    app.innerHTML = buildProfileHtml(player, jobs);

    document.getElementById('profile-back-btn')?.addEventListener('click', () => {
      navigate('/dashboard');
    });

    setupProfileWantedToggle(app);
    void mountTitlePicker(app);
    void mountPrestigeSection(app, () => { void renderProfilePage(); });
  } catch (err) {
    if (err instanceof ApiError) {
      app.innerHTML = `<div style="padding:2rem;color:var(--accent-red)">${esc(err.message)}</div>`;
    } else {
      app.innerHTML = '<div style="padding:2rem;color:var(--accent-red)">Profil konnte nicht geladen werden.</div>';
    }
  }
}
